"use client";

import React, { useEffect } from 'react';

/**
 * PreloadStylesheet component
 * 
 * Enhanced version that creates stylesheet links for any preloaded CSS resources
 * that don't have corresponding stylesheets.
 */
const PreloadStylesheet: React.FC = () => {
  // Function to fix preloaded stylesheets
  const fixPreloadedStylesheets = () => {
    // Find all preloaded CSS links
    const preloadLinks = Array.from(document.querySelectorAll('link[rel="preload"][href*=".css"]'));
    
    if (preloadLinks.length > 0) {
      console.log(`PreloadStylesheet: Found ${preloadLinks.length} preloaded CSS files`);
    }
    
    // Process each preload link
    preloadLinks.forEach(preloadLink => {
      const href = preloadLink.getAttribute('href');
      if (!href) return;
      
      // Ensure the preload link has 'as="style"' attribute
      if (!preloadLink.getAttribute('as')) {
        console.log(`PreloadStylesheet: Adding missing 'as="style"' attribute to ${href}`);
        preloadLink.setAttribute('as', 'style');
      }
      
      // Check if corresponding stylesheet link already exists
      const stylesheetExists = Array.from(
        document.querySelectorAll('link[rel="stylesheet"]')
      ).some(link => link.getAttribute('href') === href);
      
      // If no corresponding stylesheet, create one
      if (!stylesheetExists) {
        console.log(`PreloadStylesheet: Creating stylesheet link for ${href}`);
        const stylesheetLink = document.createElement('link');
        stylesheetLink.rel = 'stylesheet';
        stylesheetLink.href = href;
        document.head.appendChild(stylesheetLink);
      }
    });
  };

  useEffect(() => {
    // Run immediately
    fixPreloadedStylesheets();
    
    // Run again after a short delay to catch any dynamically added preloads
    const initialTimeout = setTimeout(fixPreloadedStylesheets, 100);
    
    // Also set up a MutationObserver to watch for new link elements
    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'childList' && 
            mutation.addedNodes.length > 0 &&
            Array.from(mutation.addedNodes).some(node => 
              node.nodeName === 'LINK' && 
              (node as HTMLLinkElement).rel === 'preload' && 
              (node as HTMLLinkElement).href.includes('.css')
            )) {
          fixPreloadedStylesheets();
          break;
        }
      }
    });
    
    // Start observing the document head for changes
    observer.observe(document.head, { childList: true, subtree: true });
    
    return () => {
      clearTimeout(initialTimeout);
      observer.disconnect();
    };
  }, []);
  
  return null;
};

export default PreloadStylesheet; 